import { useEffect, useState } from 'react'
import { Alert, Button, Col, Form, Row, Spinner } from 'react-bootstrap'

import "./Register.scss"
import { fetchCityListData, fetchProvinceListData } from '../../services/GeoServices';
import useValidation from '../../services/useValidation';
import { createUser } from '../../services/UserServices';
import ReactRecaptcha3 from 'react-google-recaptcha3';
import { useNavigate } from 'react-router-dom';

export default function Register() {
  const [ provinces, setProvinces ] = useState([]);
  const [ cities, setCities ] = useState([]);
  const phoneValidation = useValidation('','phone');
  const emailValidation = useValidation('','email');
  const [loading, setLoading] = useState(true);
  const [validated, setValidated] = useState(false)
  const [userSelected, setUserSelected] = useState({
    name: '',
    lastname: '',
    razonSocial: '',
    cuit: '',
    rubro: '',
    rubroOtro: '',
    negocioLink: '',
    fechaNacimiento: '',
    province_id: 0,
    city_id: '',
    subscribe: true,
    terms: false,
    password: '',
    passwordConfirm: ''
  })

  const rubrosDisponibles = [
    'Librería',
    'Bazar',
    'Kiosco',
    'Polirubro',
    'Institución educativa',
    'Empresa privada',
    'Revendedor',
    'Otro'
  ];
  const [message, setMessage] = useState('');
  const [visible, setVisible] = useState(false);
  const [showPasswordError, setShowPasswordError ] = useState(false);
  const [showCuitError, setShowCuitError] = useState(false);
  const navigate = useNavigate()

  // Prefijos de CUIT que no son persona física "de a pie": monotributista
  // (20/23/24/27) o persona jurídica (30/33/34). Este registro es solo
  // para comercios/instituciones, no para consumidores finales.
  const prefijosCuitValidos = ['20', '23', '24', '27', '30', '33', '34'];

  const isCuitValid = (cuit) => {
    const soloNumeros = cuit.replace(/\D/g, '');
    return soloNumeros.length === 11 && prefijosCuitValidos.includes(soloNumeros.slice(0, 2));
  }

  const handleCuitChange = (e) => {
    let value = e.target.value.replace(/\D/g, '');
    if (value.length > 2 && value.length <= 10) {
      value = `${value.slice(0, 2)}-${value.slice(2)}`;
    } else if (value.length > 10) {
      value = `${value.slice(0, 2)}-${value.slice(2, 10)}-${value.slice(10, 11)}`;
    }
    setUserSelected(prev => ({ ...prev, cuit: value }));
    setShowCuitError(false);
  }

  const create = async () => {
    var f = new FormData();
    f.append("nombre", userSelected.name);
    f.append("apellido", userSelected.lastname);
    f.append("razon_social", userSelected.razonSocial);
    f.append("cuit", userSelected.cuit);
    f.append("rubro", userSelected.rubro === 'Otro' ? userSelected.rubroOtro : userSelected.rubro);
    f.append("negocio_link", userSelected.negocioLink);
    f.append("fecha_nacimiento", userSelected.fechaNacimiento);
    f.append("email", emailValidation.value);
    f.append("password", userSelected.password);
    f.append("passwordConfirm", userSelected.passwordConfirm);
    f.append("telefono", phoneValidation.value);
    f.append("provincia_id", userSelected.province_id);
    f.append("ciudad_id", userSelected.city_id);
    f.append("subscribe", userSelected.subscribe ? 1 : 0);
    f.append("terms", userSelected.terms ? 1 : 0);
    setVisible(false);
    ReactRecaptcha3.getToken().then(()=>{
      setLoading(true);
      createUser(f,'c')
        .then(data => {
          navigate("/registro-confirmado");
        })
        .catch(err => {
          setVisible(true);
          setMessage(err.response.data);
        })
        .finally(() => setLoading(false)); 
    })
  }

  const handleSubmit = (event) => {
    const form = event.currentTarget;
    event.preventDefault();
    event.stopPropagation();
    if (!isCuitValid(userSelected.cuit)) {
      setShowCuitError(true);
      setMessage('Este registro es exclusivo para comercios, empresas o instituciones. Ingresá un CUIT válido.')
      setVisible(true);
      setValidated(true);
      return;
    }
    if (form.checkValidity()) {
      create();
    } else {
      setMessage('Completa los campos obligatorios')
      setVisible(true);
    }
    setValidated(true);
  };

  const handlePasswordConfirm = (e) => {
    const value = e.target.value;
    setUserSelected({
      ...userSelected,
      passwordConfirm: value,
    });

    if (userSelected.password && userSelected.password !== value) {
      setShowPasswordError(true);
    } else {
      setShowPasswordError(false);
    }
  }

  useEffect(()=>{
    if (userSelected.province_id > 0) {
      fetchCityListData(userSelected.province_id)
      .then((data) => {
        setCities(data);
      })
      .catch(err => console.log(err))
      .finally(() => {setLoading(false)});
    }
  },[userSelected.province_id])

  useEffect(() => {
    window.scrollTo(0, 0);
    fetchProvinceListData()
      .then((data) => {
        setProvinces(data);
      })
      .catch(err => console.log(err))
      .finally(() => {setLoading(false)});
  }, []);

  return (
    <div className="form-content register">
      <Alert variant="danger" dismissible show={visible} onClose={() => setVisible(false)}>
        {message}
      </Alert>
      {loading
        ? <Spinner />
        : <>
            <div className='block register-form'>
              <div className='title-content'>
                <div className='title'>Registrate</div>
                <div className='subtitle'>Registrate para poder realizar las compras que quieras.</div>
              </div>
              <Alert variant="warning" className="register-notice">
                Estimados, nuestra plataforma mayorista está pensada para comercios, empresas e instituciones.
                <br /><br />
                Si sos cliente particular, podés encontrar todos nuestros productos en nuestra tienda minorista:{' '}
                <a href="https://tienda.libreriadelcolegio.com" target="_blank" rel="noopener noreferrer">
                  tienda.libreriadelcolegio.com
                </a>
              </Alert>
              <Form noValidate validated={validated} onSubmit={handleSubmit}>
                <Row className="mb-3">
                  <Form.Group as={Col} controlId="formGridRazonSocial" xs={12} md={6}>
                    <Form.Label>Razón Social / Nombre del comercio</Form.Label>
                    <Form.Control
                      type="text"
                      placeholder='Ingrese la razón social o nombre del comercio...'
                      value={userSelected.razonSocial}
                      onChange={(e) => {
                        setUserSelected({
                          ...userSelected,
                          razonSocial: e.target.value,
                        });
                      }}
                      required/>
                    <Form.Control.Feedback type="invalid">
                      Ingresa la razón social o nombre del comercio.
                    </Form.Control.Feedback>
                  </Form.Group>

                  <Form.Group as={Col} controlId="formGridCuit" xs={12} md={6}>
                    <Form.Label>CUIT</Form.Label>
                    <Form.Control
                      type="text"
                      placeholder='Ej: 30-12345678-9'
                      value={userSelected.cuit}
                      onChange={handleCuitChange}
                      maxLength={13}
                      isInvalid={showCuitError}
                      required/>
                    <Form.Control.Feedback type="invalid">
                      Ingresá un CUIT válido de comercio/institución (no de persona física consumidor final).
                    </Form.Control.Feedback>
                  </Form.Group>
                </Row>
                <Row className="mb-3">
                  <Form.Group as={Col} controlId="formGridRubro" xs={12} md={userSelected.rubro === 'Otro' ? 6 : 12}>
                    <Form.Label>Rubro / Tipo de comercio</Form.Label>
                    <Form.Select
                      value={userSelected.rubro}
                      onChange={(e) => {
                        setUserSelected({
                          ...userSelected,
                          rubro: e.target.value,
                        });
                      }}
                      required>
                      <option value=""></option>
                      {rubrosDisponibles.map((item) => (
                        <option key={`rubro_${item}`} value={item}>{item}</option>
                      ))}
                    </Form.Select>
                    <Form.Control.Feedback type="invalid">
                      Debe seleccionar el rubro de su comercio.
                    </Form.Control.Feedback>
                  </Form.Group>

                  {userSelected.rubro === 'Otro' &&
                    <Form.Group as={Col} controlId="formGridRubroOtro" xs={12} md={6}>
                      <Form.Label>Especifique el rubro</Form.Label>
                      <Form.Control
                        type="text"
                        placeholder='Cuéntenos brevemente a qué se dedica su comercio...'
                        value={userSelected.rubroOtro}
                        onChange={(e) => {
                          setUserSelected({
                            ...userSelected,
                            rubroOtro: e.target.value,
                          });
                        }}
                        required/>
                      <Form.Control.Feedback type="invalid">
                        Cuéntenos a qué se dedica su comercio.
                      </Form.Control.Feedback>
                    </Form.Group>
                  }
                </Row>
                <Row className="mb-3" >
                  <Form.Group as={Col} controlId="formGridName" xs={12} md={6}>
                    <Form.Label>Nombre de contacto</Form.Label>
                    <Form.Control
                      type="text"
                      placeholder='Nombre de la persona de contacto...'
                      value={userSelected.name}
                      onChange={(e) => {
                        setUserSelected({
                          ...userSelected,
                          name: e.target.value,
                        });
                      }}
                      required/>
                    <Form.Control.Feedback type="invalid">
                      Ingresa un nombre.
                    </Form.Control.Feedback>
                  </Form.Group>

                  <Form.Group as={Col} controlId="formGridLastname" xs={12} md={6}>
                    <Form.Label>Apellido de contacto</Form.Label>
                    <Form.Control
                      type="text"
                      placeholder="Apellido de la persona de contacto..."
                      value={userSelected.lastname}
                      onChange={(e) => {
                        setUserSelected({
                          ...userSelected,
                          lastname: e.target.value,
                        });
                      }}
                      required/>
                    <Form.Control.Feedback type="invalid">
                      Ingresa un apellido.
                    </Form.Control.Feedback>
                  </Form.Group>
                </Row>

                <Row className="mb-3">
                  <Form.Group as={Col} controlId="formGridPhone" xs={12} md={6}>
                    <Form.Label>Teléfono/WhatsApp</Form.Label>
                    <Form.Control 
                      type="number"
                      placeholder='Ingrese su teléfono/whatsApp...'
                      value={phoneValidation.value}
                      onChange={phoneValidation.handleChange}
                      invalid={!phoneValidation.isValid}
                      required/>
                    <Form.Control.Feedback type="invalid">
                      {phoneValidation.errorMessage !== '' ? phoneValidation.errorMessage : "Debe ingresar el teléfono"}
                    </Form.Control.Feedback>
                  </Form.Group>

                  <Form.Group as={Col} controlId="formGridEmail">
                    <Form.Label>E-mail</Form.Label>
                    <Form.Control
                      type="email"
                      placeholder="Ingrese su email..."
                      value={emailValidation.value}
                      onChange={emailValidation.handleChange}
                      invalid={!emailValidation.isValid}
                      required
                    />
                    <Form.Control.Feedback type="invalid">
                      {emailValidation.errorMessage !== '' ? emailValidation.errorMessage : "Debe ingresar el e-mail"}
                    </Form.Control.Feedback>
                    <Form.Text className="field-hint">
                      Este e-mail va a ser utilizado para ingresar a la web.
                    </Form.Text>
                </Form.Group>
                </Row>

                <Row className="mb-3">
                  <Form.Group as={Col} controlId="formGridNegocioLink" xs={12} md={6}>
                    <Form.Label>Negocio en Google Maps / Instagram</Form.Label>
                    <Form.Control
                      type="text"
                      placeholder='Link de su negocio (opcional)...'
                      value={userSelected.negocioLink}
                      onChange={(e) => {
                        setUserSelected({
                          ...userSelected,
                          negocioLink: e.target.value,
                        });
                      }}/>
                  </Form.Group>

                  <Form.Group as={Col} controlId="formGridFechaNacimiento" xs={12} md={6}>
                    <Form.Label>Fecha de nacimiento</Form.Label>
                    <Form.Control
                      type="date"
                      value={userSelected.fechaNacimiento}
                      onChange={(e) => {
                        setUserSelected({
                          ...userSelected,
                          fechaNacimiento: e.target.value,
                        });
                      }}/>
                  </Form.Group>
                </Row>

                <Row className="mb-3">
                  <Form.Group as={Col} controlId="formGridPassword" xs={12} md={6}>
                    <Form.Label>Contraseña</Form.Label>
                    <Form.Control
                      type="password"
                      placeholder='Ingrese una contraseña...'
                      value={userSelected.password}
                      onChange={(e) => {
                        setUserSelected({
                          ...userSelected,
                          password: e.target.value,
                        });
                      }}
                      minLength={8}
                      required
                      />
                      <Form.Control.Feedback type="invalid">
                      Debe ingresar una contraseña. Debe tener mínimo 8 caracteres.
                    </Form.Control.Feedback>
                  </Form.Group>

                  <Form.Group as={Col} controlId="formGridConfirmPassword">
                    <Form.Label>Confirmar Contraseña</Form.Label>
                    <Form.Control
                      type="password"
                      placeholder="Ingrese nuevamente la contraseña..."
                      value={userSelected.passwordConfirm}
                      onChange={handlePasswordConfirm}
                      minLength={8}
                      isInvalid={showPasswordError}
                      required
                    />
                    <Form.Control.Feedback type="invalid">
                      {showPasswordError ? 'Las contraseñas no coinciden.' : "Debe ingresar una contraseña. Debe tener mínimo 8 caracteres."}
                    </Form.Control.Feedback>
                  </Form.Group>
                </Row>

                <Row className="mb-3">
                  <Form.Group as={Col} controlId="formGridState" xs={12} md={6}>
                    <Form.Label>Provincia</Form.Label>
                    <Form.Select
                      required
                      placeholder="Elija su provincia..."
                      value={userSelected.province_id}
                      onChange={(e) => {
                        setUserSelected({
                          ...userSelected, 
                          province_id: Number(e.target.value),
                          city_id: 0
                        })
                      }}>
                      <option></option>
                      {provinces.length > 0 && 
                        provinces.map((item) => {
                          return <option key={`prov_${item.id}`} value={item.id}>{item.nombre}</option>
                        })
                      }
                    </Form.Select>
                    <Form.Control.Feedback type="invalid">
                      Debe elegir una provincia.
                    </Form.Control.Feedback>
                  </Form.Group>

                  <Form.Group as={Col} controlId="formGridCity">
                    <Form.Label>Ciudad</Form.Label>
                    <Form.Select
                      required
                      placeholder="Elija su ciudad..."
                      value={userSelected.city_id}
                      onChange={(e) => {
                        setUserSelected({
                          ...userSelected, 
                          city_id: Number(e.target.value)
                        })
                      }}
                      disabled={userSelected.province_id === 0}>
                        <option></option>
                        {cities.length > 0 && 
                          cities.map((item) => {
                            return <option key={`city_${item.id}`} value={item.id}>{item.nombre}</option>
                          })
                        }
                    </Form.Select>
                    <Form.Control.Feedback type="invalid">
                      Debe elegir una ciudad.
                    </Form.Control.Feedback>
                  </Form.Group>
                </Row>
                <div className='register-footer'>
                  <Row>
                    <div className='note'>
                      Su cuenta va a ser revisada por nuestro equipo antes de habilitarse (verificamos que se trate de un comercio, empresa o institución).
                      Una vez completado el formulario recibirá un correo electrónico en su casilla de mail para confirmar su registración.
                      Si el mismo no llega, como primer paso busque en Correo no deseado o SPAM.
                      De no encontrarlo, comuníquese con nosotros a info@libreriadelcolegio.com y nos ocuparemos de activar la cuenta.
                    </div>
                  </Row>
                  <div>
                    <Row>
                      <Form.Check // prettier-ignore
                        type="checkbox"
                        id="formGridSubscribe"
                        label="Deseo suscribirme a la newsletter"
                        checked={userSelected.subscribe}
                        onChange={(e) => {
                          setUserSelected({
                            ...userSelected,
                            subscribe: e.target.checked
                          })
                        }}
                      />
                    </Row>
                    <Row>
                      <Form.Check // prettier-ignore
                        type="checkbox"
                        id="formGridTerminos"
                        label="He leido y acepto los términos y condiciones"
                        checked={userSelected.terms}
                        onChange={(e) => {
                          setUserSelected({
                            ...userSelected,
                            terms: e.target.checked
                          })
                        }}
                        required
                      />
                      <Form.Control.Feedback type="invalid">
                        Debe aceptar los terminos y condiciones.
                      </Form.Control.Feedback>
                    </Row>
                  </div>
                  <div className="btn-content">
                    <Button type='submit' color="success">Crear Cuenta</Button>
                  </div>
                </div>
              </Form>
            </div>
          </>
      }
    </div>
  )
}